import "../../styles/blocks/contact-page/tabs-product.scss";

import React, {useCallback, useEffect, useState} from 'react';
import TabForm from "./TabForm";
import Separator from '../Separetor/Separator';
import handleize from '../../helpers/handleize';
import IconSuccess from '../../icons/IconSuccess';
import {gdprFormSubmit} from "../../helpers/gdpr";
import {appendCf7RecaptchaToken} from '../../helpers/cf7Recaptcha';

const themesSupportId = 8763;
const SEPARATOR_COLOR = '#454545';
const DEFAULT_SUBMIT_MESSAGE = 'Thank you for your message. It has been sent.';
const THEME_LICENSE_OPTIONS = ['Purchased', 'Trial'];

const getThemePresets = (theme) => (
  theme?.acfThemeSupport?.presets
    ?.map(({name}) => name?.trim())
    .filter(Boolean) || []
);

const TabProductSupport = ({ tab, lists }) => {
  const {
    addFileS,
    description,
    email,
    label,
    linkToSource,
    message,
    name,
    phone,
    sendButton,
    themesLabel,
    version,
  } = tab;

  const {themesList} = lists;
  const [isSubmit, setIsSubmit] = useState(false);
  const [submitMessage, setSubmitMessage] = useState(DEFAULT_SUBMIT_MESSAGE);
  const [invalidFields, setInvalidFields] = useState({});
  const [initialSelectedProduct, setInitialSelectedProduct] = useState(null);
  const [showLists, setShowLists] = useState(false);
  const [activeProduct, setActiveProduct] = useState(null);
  const [selectedPreset, setSelectedPreset] = useState('');

  const form = {
    addFiles: addFileS,
    description,
    email,
    label,
    linkToSource,
    message,
    name,
    phone,
    sendButton,
    version,
  }

  const handleSubmit = useCallback(async (event) => {
    gdprFormSubmit(event, {formId: 'ProductSupportForm'}, async (data) => {
      try {
        await appendCf7RecaptchaToken(data);

        const response = await fetch(`/wp-json/contact-form-7/v1/contact-forms/${themesSupportId}/feedback`, {
          body: data,
          method: 'POST'
        });

        const responseJSON = await response.json();

        if (!responseJSON.status) {
          console.log(`Error: ${responseJSON.message}`);
          throw new Error('Server error');
        }

        setSubmitMessage(responseJSON?.message);

        if (responseJSON.status !== 'mail_sent') {
          const invalidMessages = responseJSON.invalid_fields.reduce((obj, currentValue) => {
            obj[currentValue.into.replace('span.wpcf7-form-control-wrap.', '')] = currentValue.message;

            return obj;
          }, {});

          setInvalidFields(invalidMessages);
          return;
        }

        setInvalidFields({});
        setIsSubmit(true);
      } catch (error) {
        console.log(`Error: ${error}`);
      }
    });
  }, []);

  const themeListMarkdown = useCallback(() => (
    <div className="cts__product-type">
      <input type="hidden" name="product_type" value={handleize(themesLabel)} />

      <h3 className="cts__product-list-heading">
        Select theme<span className="cts__required-star">*</span>
      </h3>

      <ul className="cts__list">
        {themesList?.map((product) => (
          <li className="cts__list-item" key={product.slug}>
            <label className="cts__list-item-card">
              <h2 className="cts__list-item-label">
                {product.title}
              </h2>

              <input
                type="radio"
                name={handleize(themesLabel)}
                value={product.title}
                required={true}
                defaultChecked={product.slug === initialSelectedProduct}
                onChange={event => {
                  if (event.target.checked) {
                    setActiveProduct(product);
                    setSelectedPreset('');
                  }
                }}
              />

              <div className="cts__radio" />

              <div className="cts__list-item-description">
                {product.acfTheme?.descriptionForCp}
              </div>
            </label>
          </li>
        ))}
      </ul>
    </div>
  ), [initialSelectedProduct, themesLabel, themesList]);

  const themeLicenseMarkdown = useCallback(() => {
    const presets = getThemePresets(activeProduct);

    return (
      <div className="cts__theme-license">
        <h3 className="cts__theme-license-heading">
          Theme license<span className="cts__required-star">*</span>
        </h3>

        <ul className="cts__theme-license-options">
          <li className="cts__theme-license-option cts__theme-license-option--preset">
            <label className="cts__preset-card">
              <span className="cts__visually-hidden">Preset</span>

              <span className="cts__preset-select-wrap">
                <select
                  className="cts__preset-select"
                  name="preset"
                  value={selectedPreset}
                  onChange={(event) => setSelectedPreset(event.target.value)}
                  required={true}
                  disabled={!presets.length}
                >
                  <option value="" disabled>
                    Select preset
                  </option>

                  {presets.map((preset) => (
                    <option value={preset} key={preset}>
                      {preset}
                    </option>
                  ))}
                </select>
              </span>
            </label>
          </li>

          {THEME_LICENSE_OPTIONS.map((option) => (
            <li className="cts__theme-license-option" key={option}>
              <label className="cts__theme-license-card">
                <span className="cts__theme-license-label">
                  {option}
                </span>

                <input
                  type="radio"
                  name="theme_license"
                  value={option}
                  required={true}
                />

                <div className="cts__radio" />
              </label>
            </li>
          ))}
        </ul>

        {invalidFields.preset && (
          <div className="cts__error-message">
            <p>
              {invalidFields.preset}
            </p>
          </div>
        )}

        {invalidFields.theme_license && (
          <div className="cts__error-message">
            <p>
              {invalidFields.theme_license}
            </p>
          </div>
        )}
      </div>
    );
  }, [activeProduct, invalidFields.preset, invalidFields.theme_license, selectedPreset]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const searchParams = new URLSearchParams(window.location.search);
      const theme = searchParams.get('theme');

      if (theme) {
        const activeProduct = themesList.find(({slug}) => slug === theme);
        setActiveProduct(activeProduct);
        setInitialSelectedProduct(theme);
      }
    }

    setShowLists(true);
  }, []);

  return (
    <>
      {isSubmit && (
        <div className="contact-tabs__tab contact-tabs__tab--success">
          <IconSuccess/><p className="contact-tabs__success">{submitMessage}</p>
        </div>
      )}

      {!isSubmit && (
        <div className="contact-tabs__tab">
          <form className="contact-tabs__support cts" id="ProductSupportForm" onSubmit={handleSubmit}>
            {showLists && (
              <>
                {themeListMarkdown()}
                {themeLicenseMarkdown()}
                <Separator color={SEPARATOR_COLOR}/>
              </>
            )}

            <TabForm
              form={form}
              invalidFields={invalidFields}
              activeProductType={themesLabel}
              activeProduct={activeProduct}
            />
          </form>
        </div>
      )}
    </>
  );
}

export default TabProductSupport;
